import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import api from '../api/api';
import ItemQuantityField from '../components/ItemQuantityField';
import { SearchItem } from '../components/SearchSelect';
import {
  EventVenueMenuItemType,
  EventVenueMenuView,
  SaleCatalogItem,
} from '../types';
import './CustomerEventVenueMenu.css';
import { bookingAccessHeaders } from '../utils/bookingAccessHeaders';

interface Props {
  bookingId: number;
  customerAccessToken: string;
}

interface DraftLine {
  key: number;
  itemType: EventVenueMenuItemType;
  itemId: number;
  quantity: string;
}

const menuPath = (bookingId: number) => `/event-venue-bookings/${bookingId}/menu`;

const validCapability = (value: string) => /^[A-F0-9]{64}$/.test(value);
interface MenuApiError {
  message?: string;
  title?: string;
  errors?: Record<string, string[]> | string[];
}

const getRequestErrorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError<MenuApiError | string>(error)) {
    return error instanceof Error ? error.message : fallback;
  }
  const data = error.response?.data;
  if (typeof data === 'string') return data;
  const details = !data ? ''
    : Array.isArray(data.errors)
      ? data.errors.join(' ')
      : data.errors ? Object.values(data.errors).flat().join(' ') : '';
  return data?.message || details || data?.title || fallback;
};

const isConflict = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 409;

const quantityValue = (value: string) => {
  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(?:\.\d{1,3})?$/.test(normalized)) return null;
  const quantity = Number(normalized);
  return Number.isFinite(quantity) && quantity > 0 && quantity <= 10000 ? quantity : null;
};

const toDraft = (view: EventVenueMenuView, nextKey: () => number): DraftLine[] =>
  view.proposal?.lines.map((line) => ({
    key: nextKey(),
    itemType: line.itemType,
    itemId: line.itemId,
    quantity: String(line.quantity),
  })) || [];

const CustomerEventVenueMenu: React.FC<Props> = ({ bookingId, customerAccessToken }) => {
  const [view, setView] = useState<EventVenueMenuView | null>(null);
  const [catalog, setCatalog] = useState<SaleCatalogItem[]>([]);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(false);
  const [conflictReloading, setConflictReloading] = useState(false);
  const [conflictReloadFailed, setConflictReloadFailed] = useState(false);
  const [reconciled, setReconciled] = useState(false);
  const nextKeyRef = useRef(0);
  const initializedRef = useRef(false);
  const nextKey = () => ++nextKeyRef.current;
  const draftLocked = saving || conflictReloading || (conflict && conflictReloadFailed);

  const items: SearchItem[] = useMemo(
    () => catalog.map((item) => ({
      id: `${item.itemType}:${item.id}`,
      name: `${item.name} · ${item.category}`,
    })),
    [catalog]
  );

  const loadView = async () => {
    const { data } = await api.get<EventVenueMenuView>(menuPath(bookingId), {
      headers: bookingAccessHeaders(customerAccessToken, menuPath(bookingId)),
    });
    setView(data);
    setCatalog(data.catalog);
    return data;
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!validCapability(customerAccessToken)) {
        setError('Не вдалося перевірити приватний доступ до цього бронювання.');
        setLoading(false);
        return;
      }
      try {
        const headers = bookingAccessHeaders(customerAccessToken, menuPath(bookingId));
        const menuResponse = await api.get<EventVenueMenuView>(menuPath(bookingId), { headers });
        if (!active) return;
        setCatalog(menuResponse.data.catalog);
        setView(menuResponse.data);
        setDraft(toDraft(menuResponse.data, nextKey));
        initializedRef.current = true;
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error
            ? loadError.message
            : 'Не вдалося завантажити затверджений каталог або приватне меню бронювання.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [bookingId, customerAccessToken]);

  const updateDraft = (key: number, changes: Partial<DraftLine>) => {
    if (draftLocked) return;
    setDraft((current) => current.map((line) => line.key === key ? { ...line, ...changes } : line));
    setReconciled(false);
    setError('');
  };

  const reloadConflictView = async () => {
    setConflict(true);
    setConflictReloading(true);
    setConflictReloadFailed(false);
    setReconciled(false);
    setError('');
    try {
      await loadView();
      setNotice('Меню або доступність редагування змінилися. Перевірте актуальну версію та узгодьте свої зміни.');
    } catch {
      setConflictReloadFailed(true);
      setError('Виявлено конфлікт, але актуальне меню не вдалося перезавантажити. Повторіть лише після відновлення зв’язку.');
    } finally {
      setConflictReloading(false);
    }
  };

  const saveMenu = async () => {
    if (!view || !view.canEdit || draftLocked || (conflict && !reconciled)) return;
    setError('');
    setNotice('');
    if (draft.length > 100) {
      setError('Меню може містити не більше 100 позицій.');
      return;
    }
    const identities = new Set<string>();
    for (const line of draft) {
      if (!line.itemId || !catalog.some((item) => item.itemType === line.itemType && item.id === line.itemId)) {
        setError('Оберіть кожну позицію із затвердженого каталогу продажу або видаліть її зі зміни.');
        return;
      }
      const identity = `${line.itemType}:${line.itemId}`;
      if (identities.has(identity)) {
        setError('Кожну позицію можна додати до меню лише один раз.');
        return;
      }
      identities.add(identity);
      if (quantityValue(line.quantity) === null) {
        setError('Кількість має бути більшою за 0 і не більшою за 10 000, максимум із трьома знаками після коми.');
        return;
      }
    }

    setSaving(true);
    try {
      const { data } = await api.put<EventVenueMenuView>(
        menuPath(bookingId),
        {
          version: view.version,
          contextVersion: view.contextVersion,
          lines: draft.map((line) => ({
            itemType: line.itemType,
            itemId: line.itemId,
            quantity: quantityValue(line.quantity),
          })),
        },
        { headers: bookingAccessHeaders(customerAccessToken, menuPath(bookingId)) }
      );
      setView(data);
      setDraft(toDraft(data, nextKey));
      setConflict(false);
      setReconciled(false);
      setNotice('Зміни меню збережено.');
    } catch (requestError) {
      if (isConflict(requestError)) {
        await reloadConflictView();
      } else {
        setError(getRequestErrorMessage(
          requestError,
          'Не вдалося зберегти меню. Перевірте позиції та повторіть спробу.'
        ));
      }
    } finally {
      setSaving(false);
    }
  };

  const reconcileWithServer = () => {
    if (!view) return;
    setDraft(toDraft(view, nextKey));
    setConflict(false);
    setReconciled(false);
    setError('');
    setNotice('Поточне збережене меню завантажено у форму.');
  };

  const unitFor = (itemType: EventVenueMenuItemType) => itemType === 'dish' ? 'порцій' : 'од.';

  return (
    <section className="customer-booking-menu" aria-labelledby="customer-booking-menu-title">
      <h3 id="customer-booking-menu-title">Меню події</h3>
      {loading ? <p>Завантаження приватного меню...</p> : null}
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      {view && (
        <>
          <p>
            {view.venueName} · {view.eventDate} · {view.startTime.slice(0, 5)}–{view.endTime.slice(0, 5)}
          </p>
          <p>
            {view.canEdit
              ? `Редагування доступне до початку ${view.editDeadlineDate} за київським часом.`
              : `Редагування наразі недоступне. Строк: до початку ${view.editDeadlineDate} за київським часом.`}
          </p>
          {view.proposal && (
            <div className="customer-booking-menu__snapshot">
              <h4>Останнє збережене меню</h4>
              {view.proposal.lines.length === 0 ? <p>Меню порожнє.</p> : (
                <ul>
                  {view.proposal.lines.map((line, index) => (
                    <li key={`${line.itemType}-${line.itemId}-${index}`}>
                      {line.name} — {line.quantity} {line.unit} × {line.unitPrice.toFixed(2)} ₴ = {line.lineTotal.toFixed(2)} ₴
                    </li>
                  ))}
                </ul>
              )}
              <strong>Разом: {view.proposal.totalPrice.toFixed(2)} ₴</strong>
              {view.proposal.bookingVersion < view.version && (
                <p>Збережене меню попередньої версії бронювання ({view.proposal.bookingVersion}); поточна версія {view.version}.</p>
              )}
            </div>
          )}
          {conflict && (
            <div className="customer-booking-menu__conflict" role="alert">
              {conflictReloading ? (
                <p>Завантаження актуальної версії меню...</p>
              ) : conflictReloadFailed ? (
                <>
                  <p>Актуальну версію ще не завантажено. Перевірте з’єднання та оновіть меню перед продовженням.</p>
                  <button type="button" onClick={reloadConflictView} disabled={saving || conflictReloading}>
                    Повторити завантаження актуального меню
                  </button>
                </>
              ) : (
                <>
                  <p>Сервер завантажив актуальну версію. Автоматичного повторного збереження не буде.</p>
                  <button type="button" onClick={reconcileWithServer} disabled={draftLocked}>
                    Замінити чернетку збереженим меню
                  </button>
                  <button type="button" onClick={() => setReconciled(true)} disabled={draftLocked}>
                    Перевірив актуальну версію — залишити мої зміни
                  </button>
                </>
              )}
            </div>
          )}
          {view.canEdit && !loading && (
            <div className="customer-booking-menu__editor">
              {draft.map((line) => {
                const selected = catalog.find((item) => item.itemType === line.itemType && item.id === line.itemId);
                const selectedOption = selected ? `${selected.itemType}:${selected.id}` : '';
                return (
                  <article className="customer-booking-menu__row" key={line.key}>
                    <ItemQuantityField
                      items={items}
                      value={selectedOption}
                      onChange={(value) => {
                        const selectedItem = catalog.find((item) => `${item.itemType}:${item.id}` === value);
                        if (selectedItem) updateDraft(line.key, {
                          itemType: selectedItem.itemType,
                          itemId: selectedItem.id,
                        });
                      }}
                      quantity={line.quantity}
                      onQuantityChange={(quantity) => updateDraft(line.key, { quantity })}
                      label="Затверджена позиція"
                      ariaLabel="Пошук затвердженої позиції"
                      quantityLabel="Кількість"
                      unit={unitFor(line.itemType)}
                      quantityInputType="text"
                      quantityInputMode="decimal"
                      disabled={draftLocked}
                    />
                    {!selected && line.itemId > 0 && (
                      <p className="customer-booking-menu__unavailable">
                        Цієї позиції більше немає в затвердженому каталозі. Видаліть її або замініть, щоб зберегти меню.
                      </p>
                    )}
                    <button
                      className="customer-booking-menu__remove"
                      type="button"
                      disabled={draftLocked}
                      onClick={() => {
                        if (draftLocked) return;
                        setDraft((current) => current.filter((item) => item.key !== line.key));
                        setReconciled(false);
                      }}
                      aria-label="Видалити позицію"
                    >
                      Видалити
                    </button>
                  </article>
                );
              })}
              <div className="customer-booking-menu__actions">
                <button
                  type="button"
                  onClick={() => {
                    if (draftLocked) return;
                    setDraft((current) => [...current, {
                      key: nextKey(),
                      itemType: 'dish',
                      itemId: 0,
                      quantity: '',
                    }]);
                  }}
                  disabled={draftLocked}
                >
                  Додати позицію
                </button>
                <button
                  type="button"
                  onClick={saveMenu}
                  disabled={draftLocked || (conflict && !reconciled)}
                >
                  {saving ? 'Збереження...' : 'Зберегти меню'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default CustomerEventVenueMenu;
